import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import gameService from '../../../services/gameService';
import AlertModal from '../../common/AlertModal';
import { Button, Card, Spinner, InputGroup, FormControl, Row, Col, Modal, Alert } from 'react-bootstrap';
import { FaHandPaper, FaPlay, FaMoneyBillWave, FaTrophy, FaQuestionCircle, FaRegHandRock, FaHistory, FaInfoCircle, FaCoins, FaCrown, FaArrowLeft, FaRedoAlt, FaSadTear, FaBalanceScale, FaTimesCircle } from 'react-icons/fa';
import styles from '../BlackJack/BlackJack.module.css';

const QUICK_CHIPS = [1,5,10,25];

const EuropeanBlackJack = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [betAmount, setBetAmount] = useState(1);
  const [playerHand, setPlayerHand] = useState([]);
  const [dealerHand, setDealerHand] = useState([]);
  const [playerScore, setPlayerScore] = useState(0);
  const [dealerScore, setDealerScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gameInProgress, setGameInProgress] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [history, setHistory] = useState([]);
  const [showResultModal, setShowResultModal] = useState(false);
  const [newBalance, setNewBalance] = useState(null);
  const [resumedMsg, setResumedMsg] = useState('');
  const [showConfirmAbort, setShowConfirmAbort] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorText, setErrorText] = useState('');

  const getCardImage = (card) => `${process.env.PUBLIC_URL}/images/games/cards/${card}.png`;

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const data = await gameService.getEuropeanBlackJackHistory();
      setHistory(data || []);
    } catch {
      setErrorText(t('blackjack.errorHistory'));
      setShowErrorModal(true);
    } finally { setLoading(false); }
  };
  useEffect(()=>{ if (showHistoryModal) fetchHistory(); }, [showHistoryModal]); // eslint-disable-line

  // Captura y muestra modal para 403 y otros errores
  const handleBackendError = (err) => {
    if (err?.code === 'GAME_UNAVAILABLE') {
      setErrorText(t('game.unavailable') || 'Este juego no está disponible en este momento.');
      setShowErrorModal(true);
      return;
    }
    const backendMessage = err?.error || err?.message || t('blackjack.error_processing');
    if (String(backendMessage).includes('Insufficient balance') || String(backendMessage).includes('Saldo insuficiente')) {
      setErrorText(`${t('blackjack.insufficient_balance')} — ${t('blackjack.reload_balance')}`);
    } else {
      setErrorText(String(backendMessage));
    }
    setShowErrorModal(true);
  };

  // Reanudar automáticamente si hay ronda abierta
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await gameService.resumeEuropeanBlackJack?.();
        if (data) {
          setPlayerHand(data.playerHand || []);
          setDealerHand(data.dealerHand || []);
          setPlayerScore(data.playerScore ?? 0);
          setDealerScore(data.dealerScore ?? 0);
          setNewBalance(data.newBalance ?? null);
          if (typeof data.betAmount === 'number') setBetAmount(data.betAmount);
          setGameInProgress(true);
          setGameOver(false);
          setWinner(null);
          setShowResultModal(false);
          setResumedMsg(t('blackjack.resumed') || 'Ronda reanudada.');
        }
      } catch (err) {
        handleBackendError(err);   // <— ahora captura 403 aquí también
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startGame = async () => {
    setLoading(true); setResumedMsg('');
    try {
      const res = await gameService.playEuropeanBlackJack(betAmount);
      if (res?.resumed) {
        setBetAmount(res.betAmount ?? betAmount);
        setResumedMsg(t('blackjack.resumed') || 'Ronda reanudada.');
      }
      setGameInProgress(true);
      setPlayerHand(res.playerHand || []);
      setDealerHand(res.dealerHand || []);
      setPlayerScore(res.playerScore ?? 0);
      setDealerScore(res.dealerScore ?? 0);
      setWinner(null);
      setGameOver(false);
      setNewBalance(res.newBalance ?? null);
      setShowResultModal(false);
    } catch (e) {
      handleBackendError(e);
      setGameInProgress(false);
    } finally { setLoading(false); }
  };

  const hit = async () => {
    if (!gameInProgress || gameOver) return;
    setLoading(true);
    try {
      const res = await gameService.hitEuropeanBlackJack();
      setPlayerHand(res.playerHand || []);
      setPlayerScore(res.playerScore ?? 0);

      if ((res.playerScore ?? 0) > 21 || res.gameOver) {
        setWinner(res.result || 'dealer');
        setGameOver(true);
        setGameInProgress(false);
        setNewBalance(res.newBalance ?? null);
        if (Array.isArray(res.dealerHand)) setDealerHand(res.dealerHand);
        if (typeof res.dealerScore === 'number') setDealerScore(res.dealerScore);
        setShowResultModal(true);
      }
    } catch (e) { handleBackendError(e); }
    finally { setLoading(false); }
  };

  const stand = async () => {
    if (!gameInProgress || gameOver) return;
    setLoading(true);
    try {
      const res = await gameService.standEuropeanBlackJack();
      setDealerHand(res.dealerHand || []);
      setDealerScore(res.dealerScore ?? 0);
      setWinner(res.result || null);
      setGameOver(true);
      setGameInProgress(false);
      setNewBalance(res.newBalance ?? null);
      setShowResultModal(true);
    } catch (e) { handleBackendError(e); }
    finally { setLoading(false); }
  };

  const abortRound = async () => {
    if (!gameInProgress || gameOver) return;
    setLoading(true);
    try {
      const out = await gameService.abortEuropeanBlackJack(false);
      if (out?.aborted) restartUI();
      else { setErrorText(t('blackjack.abort_failed') || 'No se pudo abandonar la ronda.'); setShowErrorModal(true); }
    } catch (err) {
      const msg = err?.error || err?.message || '';
      if (String(msg).toLowerCase().includes('no open round')) { restartUI(); return; }
      handleBackendError(err);
    } finally { setLoading(false); }
  };

  const restartUI = () => {
    setBetAmount(1);
    setPlayerHand([]); setDealerHand([]);
    setPlayerScore(0); setDealerScore(0);
    setWinner(null); setGameOver(false);
    setGameInProgress(false); setShowResultModal(false);
    setNewBalance(null); setResumedMsg('');
  };

  const getWinnerMessage = () => {
    if (winner === 'player') return (<span className={styles.winText}><FaTrophy className="me-2" />{t('blackjack.youWin')}</span>);
    if (winner === 'dealer') return (<span className={styles.loseText}><FaSadTear className="me-2" />{t('blackjack.dealerWins')}</span>);
    if (winner === 'draw')   return (<span className={styles.drawText}><FaBalanceScale className="me-2" />{t('blackjack.draw')}</span>);
    return null;
  };

  const addChip = (v) => setBetAmount((p)=> Math.max(1, p+v));
  const setChip = (v) => setBetAmount(v);

  return (
    <div className={`${styles.container} d-flex justify-content-center align-items-center`}>
      <Card className={`${styles.table} text-center shadow-lg`}>
        <Card.Body>
          <div className="position-relative mb-4">
            <Button variant="secondary" onClick={() => navigate('/sessions')} className={styles.backButton} title={t('blackjack.back')}>
              <FaArrowLeft />
            </Button>
            <h1 className={`${styles.title} text-center`}>
              <FaCrown className="me-2" /> {t('blackjack.europeanBlackjackTitle') || 'European Blackjack'} <FaCrown className="ms-2" />
            </h1>
          </div>

          {resumedMsg && <Alert variant="info" className="mt-2 py-2">{resumedMsg}</Alert>}

          {/* Chips */}
          <div className={styles.chipsRow} aria-label="chips">
            {QUICK_CHIPS.map((c)=>(
              <button key={c} className={`${styles.chip} ${betAmount===c?styles.chipActive:''}`} onClick={()=>setChip(c)} disabled={gameInProgress||loading} title={`x${c}`}>
                <span className={styles.chipInner}>{c}</span>
              </button>
            ))}
            <div className={styles.chipsOps}>
              <button className={styles.chipOp} onClick={()=>addChip(1)} disabled={gameInProgress||loading} title="+1">+1</button>
              <button className={styles.chipOp} onClick={()=>addChip(5)} disabled={gameInProgress||loading} title="+5">+5</button>
              <button className={styles.chipOp} onClick={()=>setBetAmount(1)} disabled={gameInProgress||loading} title="Reset">↺</button>
            </div>
          </div>

          {/* Apuesta */}
          <div className="mb-3">
            <InputGroup className={styles.betGroup}>
              <InputGroup.Text className={styles.betIcon}><FaCoins /></InputGroup.Text>
              <FormControl type="number" value={betAmount} onChange={(e)=>setBetAmount(Math.max(1, Number(e.target.value)))} min="1" disabled={gameInProgress||loading} className={styles.betInput}/>
              <InputGroup.Text className={styles.betUnit}>MENEL</InputGroup.Text>
            </InputGroup>
          </div>

          {/* Acciones */}
          <div className={`${styles.actions} d-flex justify-content-around flex-wrap mb-3`}>
            <Button className={`${styles.blackjackButton} ${styles.startButton}`} onClick={startGame} disabled={loading||gameInProgress||betAmount<1}>
              {loading && !gameInProgress ? <Spinner animation="border" size="sm" /> : (<><FaPlay className="me-2" /> {t('blackjack.start')}</>)}
            </Button>
            <Button className={`${styles.blackjackButton} ${styles.restartButton}`} onClick={gameInProgress && !gameOver ? () => setShowConfirmAbort(true) : restartUI} disabled={loading} title={gameInProgress&&!gameOver?(t('blackjack.abort')||'Abandonar'):t('blackjack.restart')}>
              <FaRedoAlt className="me-2" /> {gameInProgress&&!gameOver?(t('blackjack.abort')||'Abandonar'):t('blackjack.restart')}
            </Button>
            <Button className={`${styles.blackjackButton} ${styles.historyButton}`} onClick={()=>setShowHistoryModal(true)} disabled={loading}>
              <FaHistory className="me-2" /> {t('blackjack.history')}
            </Button>
            <Button className={`${styles.blackjackButton} ${styles.rulesButton}`} onClick={()=>setShowRulesModal(true)} disabled={loading}>
              <FaInfoCircle className="me-2" /> {t('blackjack.rules')}
            </Button>
          </div>

          {/* Mesa */}
          <Row className="mt-4">
            <Col>
              <h4 className={styles.handTitle}>{t('blackjack.player')}</h4>
              <div className={`${styles.cards} d-flex justify-content-center`}>
                {playerHand.map((card, i)=>(
                  <img key={`p-${i}`} src={getCardImage(card)} alt={card} className={`${styles.cardImage} ${styles.animatedCard}`} style={{animationDelay:`${i*0.08}s`}} />
                ))}
              </div>
              <p className="mt-2">{t('blackjack.score')}: <strong>{playerScore}</strong></p>
            </Col>

            <Col>
              <h4 className={styles.handTitle}>{t('blackjack.dealer')}</h4>
              <div className={`${styles.cards} d-flex justify-content-center`}>
                {dealerHand.map((card, i)=> {
                  const isHidden = !gameOver && i>0; // modo europeo
                  return (
                    <div key={`d-${i}`} className={styles.cardFlip} data-revealed={!isHidden} style={{animationDelay:`${i*0.08}s`}}>
                      <div className={styles.cardFlipInner}>
                        <div className={`${styles.cardFace} ${styles.cardFront}`}>
                          <img src={getCardImage('BACK')} alt="Back" className={styles.cardImage}/>
                        </div>
                        <div className={`${styles.cardFace} ${styles.cardBack}`}>
                          <img src={getCardImage(card)} alt={card} className={styles.cardImage}/>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="mt-2">
                {t('blackjack.score')}: <strong>{gameOver ? dealerScore : (dealerHand.length>0 ? t('blackjack.hidden') : 0)}</strong>
              </p>
            </Col>
          </Row>

          {(gameInProgress && !gameOver) && (
            <div className="mt-4">
              <Button className={`${styles.blackjackButton} ${styles.hitButton} me-3`} onClick={hit} disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : (<><FaHandPaper className="me-2" /> {t('blackjack.hit')}</>)}
              </Button>
              <Button className={`${styles.blackjackButton} ${styles.standButton}`} onClick={stand} disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : (<><FaRegHandRock className="me-2" /> {t('blackjack.stand')}</>)}
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Resultado */}
      <Modal show={showResultModal} onHide={()=>setShowResultModal(false)} centered>
        <Modal.Header closeButton className={styles.modalHeader}><Modal.Title>{t('blackjack.resultTitle')}</Modal.Title></Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className="text-center">
            {getWinnerMessage()}
            <p className="mt-3"><strong>{t('blackjack.playerScore')}:</strong> {playerScore}</p>
            <p><strong>{t('blackjack.dealerScore')}:</strong> {dealerScore}</p>
            {newBalance !== null && (<p className="mt-3"><strong>{t('blackjack.newBalance')}:</strong> {newBalance} MENEL</p>)}
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button variant="primary" onClick={()=>setShowResultModal(false)}><FaTimesCircle className="me-2" /> {t('blackjack.close')}</Button>
        </Modal.Footer>
      </Modal>

      {/* Reglas */}
      <Modal show={showRulesModal} onHide={()=>setShowRulesModal(false)} centered>
        <Modal.Header closeButton className={styles.modalHeader}><Modal.Title><FaInfoCircle className="me-2" />{t('europeanBlackjack.rulesTitle') || 'Reglas – European Blackjack'}</Modal.Title></Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <ul className="list-unstyled">
            <li className="mb-2"><FaPlay className="me-2" /><strong>{t('blackjack.objectiveTitle')}:</strong> {t('blackjack.objective')}</li>
            <li className="mb-2"><FaMoneyBillWave className="me-2" /><strong>{t('blackjack.cardValuesTitle')}:</strong> {t('blackjack.cardValues')}</li>
            <li className="mb-2"><FaQuestionCircle className="me-2" />El crupier recibe <strong>1 carta al inicio</strong>; saca la segunda tras tu turno y roba hasta 17.</li>
            <li className="mb-2"><FaTrophy className="me-2" />Pago estándar 1:1 (sin splits/duples en esta versión).</li>
          </ul>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button variant="secondary" onClick={()=>setShowRulesModal(false)}><FaTimesCircle className="me-2" /> {t('blackjack.close')}</Button>
        </Modal.Footer>
      </Modal>

      {/* Historial */}
      <Modal show={showHistoryModal} onHide={()=>setShowHistoryModal(false)} centered>
        <Modal.Header closeButton className={styles.modalHeader}><Modal.Title><FaHistory className="me-2" />{t('blackjack.historyTitle')}</Modal.Title></Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {loading ? (
            <div className="d-flex justify-content-center py-3"><Spinner animation="border" /></div>
          ) : (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <ul className="list-group">
                {history.map((e, idx)=>{
                  const amt = Number(e.amount||0);
                  const cls = amt>0? styles.amountWin : amt<0? styles.amountLose : styles.amountDraw;
                  return (
                    <li key={idx} className={`list-group-item ${styles.historyItem}`}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{t('blackjack.result')}:</strong>{' '}
                          {e.result==='player'? t('blackjack.youWin') :
                           e.result==='dealer'? t('blackjack.dealerWins') :
                           e.result==='expired'? (t('blackjack.expired')||'Expired') :
                           e.result==='aborted'? (t('blackjack.aborted')||'Aborted') : t('blackjack.draw')}
                          <br/>
                          <strong>{t('blackjack.playerScore')}:</strong> {e.playerScore ?? 'N/A'}<br/>
                          <strong>{t('blackjack.dealerScore')}:</strong> {e.dealerScore ?? 'N/A'}<br/>
                          <strong>{t('blackjack.date')}:</strong> {new Date(e.created_at).toLocaleString()}
                        </div>
                        <div className={cls}>
                          {amt>0? `+${amt.toFixed(2)}` : amt<0? `-${Math.abs(amt).toFixed(2)}` : '0.00'} MENEL
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button variant="secondary" onClick={()=>setShowHistoryModal(false)}><FaTimesCircle className="me-2" /> {t('blackjack.close')}</Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmar abandono */}
      <AlertModal
        show={showConfirmAbort}
        onHide={()=>setShowConfirmAbort(false)}
        title={t('blackjack.abort') || 'Abandonar ronda'}
        message={t('blackjack.abortConfirm') || '¿Abandonar la ronda? Esto no reembolsa la apuesta.'}
        actions={[
          { label: t('blackjack.cancel') || 'Cancelar', variant: 'secondary', onClick: ()=>setShowConfirmAbort(false) },
          { label: t('blackjack.confirm') || 'Confirmar', variant: 'danger', onClick: async ()=>{
              setShowConfirmAbort(false);
              await abortRound();
            }
          },
        ]}
      />

      {/* Error */}
      <AlertModal show={showErrorModal} onHide={()=>setShowErrorModal(false)} title={t('blackjack.errorTitle') || 'Error'} message={errorText}/>
    </div>
  );
};

export default EuropeanBlackJack;
